import { PollyClient, SynthesizeSpeechCommand } from '@aws-sdk/client-polly';
import type { SequenceSettings, Sequence } from '../types';

const pollyClient = new PollyClient({
  region: 'us-east-1',
  credentials: {
    accessKeyId: import.meta.env.VITE_AWS_ACCESS_KEY_ID || '',
    secretAccessKey: import.meta.env.VITE_AWS_SECRET_ACCESS_KEY || ''
  }
});

class EventEmitter {
  private listeners: { [event: string]: Function[] } = {};

  on(event: string, listener: Function) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(listener);
  }

  off(event: string, listener: Function) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(l => l !== listener);
    }
  }

  emit(event: string, ...args: any[]) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(listener => listener(...args));
    }
  }
}

export const audioEvents = new EventEmitter();

// Device detection
export const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
export const isAndroid = /Android/.test(navigator.userAgent) || navigator.maxTouchPoints > 1;
export const isMac = /Macintosh/.test(navigator.userAgent) && !isIOS;
export const isSafari = typeof (window as any).safari !== 'undefined';

// Audio context setup for mobile
let audioContext: AudioContext | null = null;
let gainNode: GainNode | null = null;
let currentSource: AudioBufferSourceNode | null = null;
let currentBuffer: AudioBuffer | null = null;
let isPlaying = false;
let startTime = 0;
let pauseTime = 0;

// Default volume boost for mobile devices
const IOS_VOLUME_BOOST = 4.0;
const ANDROID_VOLUME_BOOST = 2.0;

// First, add a module-level variable to track if element playback has been used
let hasUsedElementPlayback = false;
let currentAudioElement: HTMLAudioElement | null = null;

function getVolumeBoost(): number {
  if (isIOS) return IOS_VOLUME_BOOST;
  if (isAndroid) return ANDROID_VOLUME_BOOST;
  return 1.0;
}

export function getAudioContext(): AudioContext {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    gainNode = audioContext.createGain();
    gainNode.connect(audioContext.destination);
    
    const volumeBoost = getVolumeBoost();
    if (gainNode) {
      gainNode.gain.value = volumeBoost;
    }
  }
  
  // Resume the AudioContext if it is unexpectedly suspended
  if (audioContext.state === 'suspended') {
    audioContext.resume().catch((error) => {
      console.error('Failed to resume AudioContext:', error);
      alert(`Failed to resume AudioContext: ${error}`);
      audioContext = null; // Reset to null to force re-initialization on next call
    });
  }

  return audioContext;
}

function generateAudioCacheKey(text: string, settings: SequenceSettings): string {
  return `${text}_${settings.engine || 'neural'}_${settings.voice}_${settings.language}_${settings.rate}_${settings.pitch}`;
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const base64Data = base64.includes('base64,') ? base64.split('base64,')[1] : base64;
  const binaryString = atob(base64Data);
  const length = binaryString.length;
  const bytes = new Uint8Array(length);
  for (let i = 0; i < length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

function audioBufferToWav(buffer: AudioBuffer): ArrayBuffer {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const length = buffer.length * numChannels * 2; // 16-bit audio
  const wavBuffer = new ArrayBuffer(44 + length);
  const view = new DataView(wavBuffer);

  // Helper to write strings
  const writeString = (view: DataView, offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  // RIFF chunk descriptor
  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + length, true); // file length - 8
  writeString(view, 8, "WAVE");

  // fmt subchunk
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true); // Subchunk1Size (PCM)
  view.setUint16(20, 1, true);  // AudioFormat (1 = PCM)
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true); // byte rate
  view.setUint16(32, numChannels * 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample

  // data subchunk
  writeString(view, 36, "data");
  view.setUint32(40, length, true);

  // Interleave channels
  let offset = 44;
  const channels: Float32Array[] = [];
  for (let i = 0; i < numChannels; i++) {
    channels.push(buffer.getChannelData(i));
  }

  for (let i = 0; i < buffer.length; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      let sample = channels[ch][i];
      sample = Math.max(-1, Math.min(1, sample));
      const int16 = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      view.setInt16(offset, int16, true);
      offset += 2;
    }
  }

  return wavBuffer;
}

async function createSilentAudioUrl(duration = 1) {
  const ctx = new OfflineAudioContext(2, 44100 * duration, 44100); // Use stereo for better device detection
  const buffer = ctx.createBuffer(2, ctx.length, ctx.sampleRate);
  
  // Add a very quiet sine wave to help device detection
  const channelData = buffer.getChannelData(0);
  const frequency = 440; // A4 note
  for (let i = 0; i < buffer.length; i++) {
    channelData[i] = Math.sin(2 * Math.PI * frequency * i / ctx.sampleRate) * 0.0001;
  }

  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.connect(ctx.destination);
  source.start();

  const renderedBuffer = await ctx.startRendering();
  const wav = audioBufferToWav(renderedBuffer);
  const blob = new Blob([wav], { type: 'audio/wav' });
  return URL.createObjectURL(blob);
}


function setupMediaSession() {
  if ('mediaSession' in navigator) {
    // Set metadata first to establish media session
    navigator.mediaSession.metadata = new MediaMetadata({
      title: 'Audio Playback',
      artist: 'Synthesized Speech',
      album: 'Speech Synthesis',
      artwork: [{
        src: '/assets/BarkingDogred.png',
        sizes: '512x512',
        type: 'image/png'
      }]
    });

    navigator.mediaSession.setActionHandler('play', () => {
      console.log('Media Session: Play requested');
      if (currentBuffer && audioContext && gainNode) {
        resumeAudioContext();
      }
    });

    navigator.mediaSession.setActionHandler('pause', () => {
      console.log('Media Session: Pause requested');
      pauseAudioContext();
    });

    navigator.mediaSession.setActionHandler('stop', () => {
      console.log('Media Session: Stop requested');
      stopAudioContext();
    });
  }
}

function resumeAudioContext() {
  if (!currentBuffer || !audioContext || !gainNode || isPlaying) return;

  currentSource = audioContext.createBufferSource();
  currentSource.buffer = currentBuffer;
  currentSource.connect(gainNode);

  const offset = pauseTime > 0 ? (pauseTime - startTime) / 1000 : 0;
  startTime = audioContext.currentTime - offset;
  
  currentSource.start(0, offset);
  isPlaying = true;
  audioEvents.emit('audioResume');
}

function pauseAudioContext() {
  if (!currentSource || !isPlaying) return;

  pauseTime = audioContext?.currentTime || 0;
  currentSource.stop();
  currentSource.disconnect();
  currentSource = null;
  isPlaying = false;
  audioEvents.emit('audioPause');
}

function stopAudioContext() {
  if (currentSource) {
    currentSource.stop();
    currentSource.disconnect();
    currentSource = null;
  }
  currentBuffer = null;
  isPlaying = false;
  startTime = 0;
  pauseTime = 0;
  audioEvents.emit('audioEnd');
}

async function playAudioWithContext(audioData: ArrayBuffer): Promise<void> {
  const ctx = getAudioContext();
  if (!gainNode) {
    throw new Error("Audio system not properly initialized");
  }

  // Clean up any existing playback
  stopAudioContext();
  
  audioEvents.emit('audioStart');

  try {
    currentBuffer = await ctx.decodeAudioData(audioData.slice(0));
    setupMediaSession();

    return new Promise((resolve, reject) => {
      if (!currentBuffer) {
        reject(new Error('No audio buffer available'));
        return;
      }

      currentSource = ctx.createBufferSource();
      currentSource.buffer = currentBuffer;
      currentSource.connect(gainNode!);

      currentSource.onended = () => {
        if (isPlaying) { // Only emit end if we weren't paused
          stopAudioContext();
          resolve();
        }
      };

      startTime = ctx.currentTime;
      currentSource.start();
      isPlaying = true;
    });
  } catch (error) {
    console.error('AudioContext playback failed:', error);
    alert(`AudioContext playback failed: ${error} with userAgent: ${navigator.userAgent}`);
    audioEvents.emit('audioEnd');
    throw error;
  }
}

async function playSilentAudioClip() {
  const silentUrl = await createSilentAudioUrl(1);
  const audio = new Audio(silentUrl);
  // Configure audio element for media playback. Call anything that might tell app this is not telephony
  audio.preload = 'auto';
  audio.setAttribute('x-webkit-airplay', 'allow');
  audio.setAttribute('playsinline', '');
  audio.setAttribute('controls', '');
  audio.setAttribute('data-audio-type', 'media');

  if ('mediaSession' in navigator) {
    navigator.mediaSession.metadata = new MediaMetadata({
      title: 'Audio Playback',
      artist: 'Barker App',
      album: 'Voice Sequence',
      artwork: [{
        src: '/assets/BarkingDogred.png',
        sizes: '512x512',
        type: 'image/png'
      }]
    });
  }

  // Play silent audio to initialize audio system
  try {
    await audio.play();
    await new Promise(resolve => setTimeout(resolve, 200));
    audio.pause();
  } catch (error) {
    if (error instanceof Error) {
        error.message += ' | Silent audio initialization failed';
        console.log(error.message);
        throw error; // Rethrow the modified error
    } else {
        const wrappedError = new Error(`Silent audio initialization failed: ${error}`);
        console.log(wrappedError.message);
        throw wrappedError; // Rethrow a new error if it's not an instance of Error
    }
  } finally {
    audio.src = '';
    audio.remove();
    URL.revokeObjectURL(silentUrl);
  }  
}

async function playAudioWithElement(audioData: ArrayBuffer): Promise<void> {
  audioEvents.emit('audioStart');

  try {
    const blob = new Blob([audioData], { type: 'audio/mpeg' });
    const url = URL.createObjectURL(blob);
    

    const audio = new Audio();
    
     // Configure audio element for media playback. Call anything that might tell app this is not telephony
    audio.preload = 'auto';
    audio.setAttribute('x-webkit-airplay', 'allow');
    audio.setAttribute('playsinline', '');
    audio.setAttribute('controls', '');
    audio.setAttribute('data-audio-type', 'media');
    
    let errorHandled = false;

    // Clean up previous audio element
    if (currentAudioElement) {
      currentAudioElement.pause();
      currentAudioElement.src = '';
      currentAudioElement.remove();
    }

    currentAudioElement = audio;

    // Set up media session as early as possible
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: 'Audio Playback',
        artist: 'Barker App',
        album: 'Voice Sequence',
        artwork: [{
          src: '/assets/BarkingDogred.png',
          sizes: '512x512',
          type: 'image/png'
        }]
      });

      navigator.mediaSession.setActionHandler('play', () => {
        console.log('Media Session: Play requested');
        navigator.mediaSession.playbackState = 'playing';
        audio.play().catch(error => {
          console.error('Error resuming playback:', error);
          navigator.mediaSession.playbackState = 'paused';
        });
      });

      navigator.mediaSession.setActionHandler('pause', () => {
        console.log('Media Session: Pause requested');
        navigator.mediaSession.playbackState = 'paused';
        audio.pause();
      });

      navigator.mediaSession.setActionHandler('stop', () => {
        console.log('Media Session: Stop requested');
        navigator.mediaSession.playbackState = 'none';
        audio.pause();
        audio.currentTime = 0;
      });

      navigator.mediaSession.setActionHandler('seekbackward', () => {
        const skipTime = 10;
        audio.currentTime = Math.max(audio.currentTime - skipTime, 0);
      });

      navigator.mediaSession.setActionHandler('seekforward', () => {
        const skipTime = 10;
        audio.currentTime = Math.min(audio.currentTime + skipTime, audio.duration);
      });
    }

    return new Promise((resolve, reject) => {
      const cleanup = () => {
        URL.revokeObjectURL(url);
        if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'none';
          navigator.mediaSession.metadata = null;
          ['play', 'pause', 'stop', 'seekbackward', 'seekforward'].forEach(action => {
            navigator.mediaSession.setActionHandler(action as MediaSessionAction, null);
          });
        }
        currentAudioElement = null;
      };

      audio.onended = () => {
        cleanup();
        audioEvents.emit('audioEnd');
        resolve();
      };

      audio.onpause = () => {
        console.log('Audio paused');
        if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'paused';
        }
        audioEvents.emit('audioPause');
      };

      audio.onplay = () => {
        console.log('Audio resumed');
        if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'playing';
        }
        audioEvents.emit('audioResume');
      };

      audio.onerror = (error) => {
        if (!errorHandled) {
          errorHandled = true;
          console.error('Audio element playback onerror:', error);
          alert(`Failed to play audio with element (onerror): ${error} \nuser agent ${navigator.userAgent}`);
          cleanup();
          audioEvents.emit('audioEnd');
          reject(error);
        }
      };

      // Set source and prepare for playback
      audio.src = url;
      audio.load();

      // Delay playback to allow device switching
      setTimeout(() => {
        audio.play()
          .catch(error => {
            if (!errorHandled) {
              errorHandled = true;
              console.error('Failed to play audio:', error);
              alert(`Failed to play audio with element (play): ${error} \nuser agent ${navigator.userAgent}`);
              cleanup();
              audioEvents.emit('audioEnd');
              reject(error);
            }
          });
      }, 250);
    });
  } catch (error) {
    console.error('Error in audio element playback:', error);
    alert(`Error in audio element playback: ${error}`);
    audioEvents.emit('audioEnd');
    throw error;
  }
}

async function playAudioOnMobile(audioData: ArrayBuffer): Promise<void> {
  if (!hasUsedElementPlayback && isIOS) {
    try {
      hasUsedElementPlayback = true;
      navigator.mediaDevices.ondevicechange = () => {
        navigator.mediaDevices.enumerateDevices().then(devices => {
          const audioOutputs = devices.filter(device => device.kind === 'audiooutput');
          if (audioOutputs.length > 0) {
            // New audio output device detected, trigger silent clip
            playSilentAudioClip();
          }
0        });
      };
      await playSilentAudioClip(); // Play silent audio to initialize the audio system by using the HTMLAudioElement
      await new Promise(resolve => setTimeout(resolve, 500)); // from testing, 500ms is enough for OS to switch to bluetooth
      return playAudioWithContext(audioData);
      //await playAudioWithElement(audioData); //this only gets called one time for iOS and Safari so fcn assumes this
    } catch (error) {
      console.error('Failed to play with element, falling back to context:', error);
      alert(`Failed iOS HTMLElement bluetooth switch: ${error}. Should work on speaker.`);
      return playAudioWithContext(audioData);
    }
  } else {
    return playAudioWithContext(audioData);
  }
}

async function playAudioOnDesktop(audioData: string | ArrayBuffer, settings: SequenceSettings): Promise<void> {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    
    const cleanup = () => {
      audio.onended = null;
      audio.onerror = null;
      audio.onplay = null;
      if (typeof audioData === 'string' && !audioData.startsWith('data:')) {
        URL.revokeObjectURL(audio.src);
      }
    };

    audio.onplay = () => audioEvents.emit('audioStart');
    
    audio.onended = () => {
      audioEvents.emit('audioEnd');
      cleanup();
      resolve();
    };

    audio.onerror = (error) => {
      console.error('Desktop audio playback failed:', error);
      cleanup();
      audioEvents.emit('audioEnd');
      reject(error);
    };

    if (typeof audioData === 'string') {
      if (audioData.startsWith('data:')) {
        audio.src = audioData;
      } else {
        audio.src = URL.createObjectURL(new Blob([audioData], { type: 'audio/mpeg' }));
      }
    } else {
      audio.src = URL.createObjectURL(new Blob([audioData], { type: 'audio/mpeg' }));
    }

    audio.playbackRate = settings.rate;
    audio.volume = 1.0;
    
    audio.play().catch(error => {
      cleanup();
      audioEvents.emit('audioEnd');
      reject(error);
    });
  });
}

export async function synthesizeSpeech(
  text: string,
  settings: SequenceSettings,
  sequence?: Sequence
): Promise<void> {
  if (!text) {
    console.warn('Empty text provided to synthesizeSpeech');
    return;
  }

  const cacheKey = sequence ? generateAudioCacheKey(text, settings) : '';
  
  try {
    if (sequence?.audioCache?.[cacheKey]) {
      console.log(`Using cached audio on ${isIOS ? 'iOS' : isAndroid ? 'Android' : 'Mac'} for: ${text}`);
      if (isIOS || isAndroid || isMac) {
        const audioData = base64ToArrayBuffer(sequence.audioCache[cacheKey]);
        await playAudioOnMobile(audioData);
      } else {
        await playAudioOnDesktop(sequence.audioCache[cacheKey], settings);
      }
      return;
    }

    console.log('Synthesizing new audio for:', text);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), import.meta.env.VITE_NETWORK_TIMEOUT_MS);

    try {
      const command = new SynthesizeSpeechCommand({
        Engine: settings.engine || 'neural',
        LanguageCode: settings.language,
        Text: text,
        OutputFormat: 'mp3',
        VoiceId: settings.voice,
        SampleRate: '24000',
      });

      const response = await pollyClient.send(command, { abortSignal: controller.signal });
      
      if (!response.AudioStream) {
        throw new Error('No audio stream in response');
      }

      const audioBytes = await response.AudioStream.transformToByteArray();
      
      if (sequence && cacheKey) {
        const audioBlob = new Blob([audioBytes], { type: 'audio/mpeg' });
        const base64Data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(audioBlob);
        });
        
        sequence.audioCache = {
          ...sequence.audioCache,
          [cacheKey]: base64Data
        };
      }

      if (isIOS || isAndroid || isMac) {
        await playAudioOnMobile(audioBytes.buffer);
      } else {
        await playAudioOnDesktop(audioBytes.buffer, settings);
      }
    } finally {
      clearTimeout(timeout);
    }
  } catch (error) {
    console.error(
      `Speech synthesis failed on device ${isIOS ? 'iOS' : isAndroid ? 'Android' : 'Mac'} with error:`,
      error
    );
    alert(`Speech synthesis failed: ${error}`);
    audioEvents.emit('audioEnd');
    throw error;
  }
}

async function generateAudioBase64(text: string, settings: SequenceSettings): Promise<string> {
  if (!text) {
    throw new Error('Empty text provided to generateAudioBase64');
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), import.meta.env.VITE_NETWORK_TIMEOUT_MS);

  try {
    console.log('Generating audio base64 for:', text);
    const command = new SynthesizeSpeechCommand({
      Engine: settings.engine || 'neural',
      LanguageCode: settings.language,
      Text: text,
      OutputFormat: 'mp3',
      VoiceId: settings.voice,
      SampleRate: '24000',
    });

    const response = await pollyClient.send(command, { abortSignal: controller.signal });
    
    if (!response.AudioStream) {
      throw new Error('No audio stream in response');
    }

    const audioBytes = await response.AudioStream.transformToByteArray();
    const audioBlob = new Blob([audioBytes], { type: 'audio/mpeg' });
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string); //the returned string
      reader.onerror = reject;
      reader.readAsDataURL(audioBlob); //reads Blob or File as a data: URL which is base64 string
    });
  } catch (error) {
    console.error('Audio generation failed:', error);
    alert(`Audio generation failed: ${error}`);
    throw error;
  } finally {
    clearTimeout(timeout);
  }
}

export async function preGenerateAudio(sequence: Sequence): Promise<Sequence> {
  console.log('Pre-generating audio for sequence:', sequence.name);
  
  const updatedSequence = { ...sequence };
  if (!updatedSequence.audioCache) {
    updatedSequence.audioCache = {};
  }

  if (sequence.name) {
    try {
      console.log('Generating audio for sequence name:', sequence.name);
      updatedSequence.nameAudio = await generateAudioBase64(sequence.name, sequence.settings);
      console.log('Successfully generated name audio');
    } catch (error) {
      console.error('Error generating name audio:', error);
      alert(`Error generating name audio: ${error}`);
    }
  }

  for (const text of sequence.sentences) {
    if (!text.trim()) continue;

    try {
      const cacheKey = generateAudioCacheKey(text, sequence.settings);
      
      if (updatedSequence.audioCache[cacheKey]) {
        console.log('Audio already cached for:', text);
        continue;
      }

      const audioBase64 = await generateAudioBase64(text, sequence.settings);
      updatedSequence.audioCache[cacheKey] = audioBase64;
      console.log('Cached audio for:', text);
    } catch (error) {
      console.error('Error pre-generating audio:', error);
      alert(`Error pre-generating audio: ${error}`);
    }
  }

  return updatedSequence;
}

export async function playSequenceName(sequence: Sequence): Promise<void> {
  if (!sequence.name) {
    console.warn('No sequence name to play');
    return;
  }

  console.log('Playing sequence name:', sequence.name);
  
  try {
    if (sequence.nameAudio) {
      console.log('Using pre-generated name audio');
      if (isIOS || isAndroid || isMac) {
        const audioData = base64ToArrayBuffer(sequence.nameAudio);
        await playAudioOnMobile(audioData);
      } else {
        await playAudioOnDesktop(sequence.nameAudio, sequence.settings);
      }
    } else {
      console.log('No pre-generated name audio, synthesizing directly');
      await synthesizeSpeech(sequence.name, sequence.settings);
    }
  } catch (error) {
    console.error('Error playing sequence name:', error);
    alert(`Error playing sequence name: ${error}`);
    throw error;
  }
}